import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import { formatParcours } from "../../services/aspirantUtils";

// Fonction pour calculer l'âge
function calcAge(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 && age < 130 ? `${age} ans` : "—";
}

export default function AdminAspirantsExplorer() {
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedEglise, setSelectedEglise] = useState(null);

  const [regions, setRegions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [eglises, setEglises] = useState([]);
  const [aspirants, setAspirants] = useState([]);

  const [progressCGByAspirant, setProgressCGByAspirant] = useState({});
  const [progressSYLByAspirant, setProgressSYLByAspirant] = useState({});

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");

  // --- Loaders hiérarchiques
  const loadRegions = async () => {
    setLoading(true); setErr("");
    try {
      const res = await api.get("/adminStats/regions");
      setRegions(res.data || []);
    } catch (e) {
      console.error("Erreur load regions:", e?.response || e);
      setErr(e?.response?.data?.error || "Erreur chargement régions");
    } finally { setLoading(false); }
  };

  const loadDistricts = async (regionId) => {
    setLoading(true); setErr("");
    try {
      const res = await api.get(`/adminStats/districts?region_id=${regionId}`);
      setDistricts(res.data || []);
    } catch (e) {
      console.error("Erreur load districts:", e?.response || e);
      setErr(e?.response?.data?.error || "Erreur chargement districts");
    } finally { setLoading(false); }
  };

  const loadEglises = async (districtId) => {
    setLoading(true); setErr("");
    try {
      const res = await api.get(`/adminStats/eglises?district_id=${districtId}`);
      setEglises(res.data || []);
    } catch (e) {
      console.error("Erreur load églises:", e?.response || e);
      setErr(e?.response?.data?.error || "Erreur chargement églises");
    } finally { setLoading(false); }
  };

  const loadAspirants = async (egliseId) => {
    setLoading(true); setErr("");
    try {
      const res = await api.get(`/districtStats/eglise/${egliseId}/aspirants`);
      setAspirants(res.data || []);
    } catch (e) {
      console.error("Erreur load aspirants:", e?.response || e);
      setErr(e?.response?.data?.error || "Erreur chargement aspirants");
    } finally { setLoading(false); }
  };

  // --- Progress (CG & SYL) pour l’église sélectionnée
  const loadProgressForEglise = async (egliseId) => {
    try {
      const [cg, syl] = await Promise.all([
        api.get(`/districtStats/eglise/${egliseId}/progress/CG`).then(r => r.data || []),
        api.get(`/districtStats/eglise/${egliseId}/progress/SYL`).then(r => r.data || []),
      ]);

      const toMap = (arr) => {
        const m = {};
        for (const r of arr) {
          const id = String(r.aspirant_id ?? r.id ?? r.user_id ?? "");
          const done = Number(r.done) || 0;
          const total = Number(r.total) || 0;
          let percent = Number(r.progress);
          if (!Number.isFinite(percent)) {
            percent = total > 0 ? Math.round((done / total) * 100) : 0;
          }
          m[id] = { done, total, percent: Math.max(0, Math.min(100, Math.round(percent))) };
        }
        return m;
      };

      setProgressCGByAspirant(toMap(cg));
      setProgressSYLByAspirant(toMap(syl));
    } catch (e) {
      console.error("Erreur progress église:", e?.response || e);
      setProgressCGByAspirant({});
      setProgressSYLByAspirant({});
    }
  };

  useEffect(() => { loadRegions(); }, []);

  // --- Sélections
  const onSelectRegion = (r) => {
    setSelectedRegion(r);
    setSelectedDistrict(null);
    setSelectedEglise(null);
    setDistricts([]); setEglises([]); setAspirants([]);
    loadDistricts(r.region_id);
  };

  const onSelectDistrict = (d) => {
    setSelectedDistrict(d);
    setSelectedEglise(null);
    setEglises([]); setAspirants([]);
    loadEglises(d.district_id);
  };

  const onSelectEglise = async (e) => {
    setSelectedEglise(e);
    setAspirants([]);
    setProgressCGByAspirant({});
    setProgressSYLByAspirant({});
    await loadAspirants(e.eglise_id);
    await loadProgressForEglise(e.eglise_id);
  };

  // --- Retour
  const backToRegions = () => {
    setSelectedRegion(null);
    setSelectedDistrict(null);
    setSelectedEglise(null);
    setDistricts([]); setEglises([]); setAspirants([]);
    loadRegions();
  };
  const backToDistricts = () => {
    setSelectedDistrict(null);
    setSelectedEglise(null);
    setEglises([]); setAspirants([]);
    if (selectedRegion) loadDistricts(selectedRegion.region_id);
  };
  const backToEglises = () => {
    setSelectedEglise(null);
    setAspirants([]);
    if (selectedDistrict) loadEglises(selectedDistrict.district_id);
  };

  // --- Filtre texte
  const filteredAspirants = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return aspirants;
    return aspirants.filter(a =>
      [a.aspirant_name, a.parcours].filter(Boolean).some(v => String(v).toLowerCase().includes(s))
    );
  }, [aspirants, q]);

  // --- Helpers progression
  const computeProgress = (a) => {
    const src = a.parcours === "Chef Guide" ? progressCGByAspirant : progressSYLByAspirant;
    const rec = src[String(a.id)];
    if (!rec) return { percent: 0, done: 0, total: 0, label: "0/—" };
    const done = Number(rec.done) || 0;
    const total = Number(rec.total) || 0;
    const percent = Math.max(0, Math.min(100, Number(rec.percent) || 0));
    return { percent, done, total, label: total ? `${done}/${total}` : `${done}/—` };
  };

  const progressClass = (percent) => {
    if (percent >= 100) return "bg-success";
    if (percent >= 75)  return "bg-primary";
    if (percent >= 50)  return "bg-info";
    if (percent >= 25)  return "bg-warning text-dark";
    return "bg-danger";
  };

  return (
    <div className="card shadow-sm mt-4">
      <div className="card-header bg-color text-white">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <strong>Liste aspirants</strong>
            <div className="small text-white-50">
              {selectedRegion ? (
                <>
                  <span className="text-decoration-underline" style={{ cursor: "pointer" }} onClick={backToRegions}>Régions</span>
                  {" / "}
                  {selectedDistrict ? (
                    <>
                      <span className="text-decoration-underline" style={{ cursor: "pointer" }} onClick={backToDistricts}>
                        {selectedRegion.region_name}
                      </span>
                      {" / "}
                      {selectedEglise ? (
                        <>
                          <span className="text-decoration-underline" style={{ cursor: "pointer" }} onClick={backToEglises}>
                            {selectedDistrict.district_name}
                          </span>
                          {" / "}
                          <span>{selectedEglise.eglise_name}</span>
                        </>
                      ) : (
                        <span>{selectedDistrict.district_name}</span>
                      )}
                    </>
                  ) : (
                    <span>{selectedRegion.region_name}</span>
                  )}
                </>
              ) : (
                "Régions"
              )}
            </div>
          </div>
          {selectedEglise && (
            <div className="d-flex gap-2">
              <input
                className="form-control form-control-sm"
                placeholder="Rechercher un aspirant…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          )}
        </div>
      </div>

      <div className="card-body">
        {err && <div className="alert alert-danger">{err}</div>}
        {loading && <div className="text-muted">Chargement…</div>}

        {/* --- NIVEAU 1 : REGIONS --- */}
        {!loading && !selectedRegion && (
          <div className="row g-3">
            {regions.length === 0 ? (
              <div className="text-muted">Aucune région.</div>
            ) : regions.map((r) => (
              <div key={r.region_id} className="col-md-4">
                <div
                  className="p-3 border rounded shadow-sm bg-white d-flex justify-content-between align-items-center"
                  style={{ cursor: "pointer" }}
                  onClick={() => onSelectRegion(r)}
                >
                  <div>
                    <div className="fw-semibold">{r.region_name}</div>
                    <div className="small text-muted">
                      <span className="badge bg-color me-2">CG: {r.total_CG}</span>
                      <span className="badge bg-success">SYL: {r.total_SYL}</span>
                    </div>
                  </div>
                  <i className="bi bi-chevron-right"></i>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* --- NIVEAU 2 : DISTRICTS --- */}
        {!loading && selectedRegion && !selectedDistrict && (
          <div className="row g-3">
            {districts.length === 0 ? (
              <div className="text-muted">Aucun district.</div>
            ) : districts.map((d) => (
              <div key={d.district_id} className="col-md-4">
                <div
                  className="p-3 border rounded shadow-sm bg-white d-flex justify-content-between align-items-center"
                  style={{ cursor: "pointer" }}
                  onClick={() => onSelectDistrict(d)}
                >
                  <div>
                    <div className="fw-semibold">{d.district_name}</div>
                    <div className="small text-muted">
                      <span className="badge bg-color me-2">CG: {d.total_CG}</span>
                      <span className="badge bg-success">SYL: {d.total_SYL}</span>
                    </div>
                  </div>
                  <i className="bi bi-chevron-right"></i>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* --- NIVEAU 3 : EGLISES --- */}
        {!loading && selectedDistrict && !selectedEglise && (
          <div className="row g-3">
            {eglises.length === 0 ? (
              <div className="text-muted">Aucune église.</div>
            ) : eglises.map((e) => (
              <div key={e.eglise_id} className="col-md-4">
                <div
                  className="p-3 border rounded shadow-sm bg-white d-flex justify-content-between align-items-center"
                  style={{ cursor: "pointer" }}
                  onClick={() => onSelectEglise(e)}
                >
                  <div>
                    <div className="fw-semibold">{e.eglise_name}</div>
                    <div className="small text-muted">
                      <span className="badge bg-color me-2">CG: {e.total_CG}</span>
                      <span className="badge bg-success">SYL: {e.total_SYL}</span>
                    </div>
                  </div>
                  <i className="bi bi-chevron-right"></i>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* --- NIVEAU 4 : ASPIRANTS --- */}
        {!loading && selectedEglise && (
          <>
            {/* Version desktop : tableau */}
            <div className="table-responsive d-none d-md-block">
              <table className="table table-hover table-sm align-middle">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Nom</th>
                    <th>Matricule</th>
                    <th>Âge</th>
                    <th>Parcours</th>
                    <th>Progression</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAspirants.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">Aucun élève</td>
                    </tr>
                  ) : filteredAspirants.map((a, idx) => {
                    const { percent, label } = computeProgress(a);
                    const barCls = progressClass(percent);
                    return (
                      <tr key={a.id}>
                        <td>{idx + 1}</td>
                        <td>{a.aspirant_name}</td>
                        <td>
                          {a.matricule ? (
                            <span className="badge bg-success text-white">{a.matricule}</span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>{calcAge(a.birth_date)}</td>
                        <td>{formatParcours(a.parcours)}</td>
                        <td style={{ minWidth: 260 }}>
                          <div className="small text-muted mb-1">{label} • {percent}%</div>
                          <div className="progress" style={{ height: 18 }} title={`${label} (${percent}%)`}>
                            <div
                              className={`progress-bar ${barCls}`}
                              role="progressbar"
                              style={{ width: `${percent}%` }}
                              aria-valuenow={percent}
                              aria-valuemin="0"
                              aria-valuemax="100"
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Version mobile : cartes */}
            <div className="d-md-none">
              {filteredAspirants.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  Aucun élève
                </div>
              ) : filteredAspirants.map((a, idx) => {
                const { percent, label } = computeProgress(a);
                const barCls = progressClass(percent);
                return (
                  <div key={a.id} className="card mb-3 shadow-sm">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                          <h6 className="fw-semibold mb-1">{a.aspirant_name}</h6>
                          <div className="mb-2">
                            <small className="text-muted d-block">Matricule:</small>
                            {a.matricule ? (
                              <span className="badge bg-success text-white">{a.matricule}</span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </div>
                        </div>
                        <div>
                          <span className="badge bg-info text-dark">{formatParcours(a.parcours)}</span>
                        </div>
                      </div>
                      
                      <div className="mb-2">
                        <small className="text-muted d-block">Âge:</small>
                        <span>{calcAge(a.birth_date)}</span>
                      </div>
                      
                      <div className="mb-2">
                        <small className="text-muted d-block mb-1">Progression:</small>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <small className="text-muted">{label} • {percent}%</small>
                        </div>
                        <div className="progress" style={{ height: 18 }} title={`${label} (${percent}%)`}>
                          <div
                            className={`progress-bar ${barCls}`}
                            role="progressbar"
                            style={{ width: `${percent}%` }}
                            aria-valuenow={percent}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary btn-sm" onClick={backToEglises}>
                ← Églises
              </button>
              <button className="btn btn-outline-secondary btn-sm" onClick={backToDistricts}>
                ← Districts
              </button>
              <button className="btn btn-outline-secondary btn-sm" onClick={backToRegions}>
                ← Régions
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

